import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Building, BookOpen, Users, FileText, ArrowLeft, 
  Loader, ChevronRight, GraduationCap, Mail, Phone 
} from 'lucide-react';
import Header from '../components/common/Header';
import { publicAPI } from '../services/api';

const SchoolPage = () => {
  const { id } = useParams();
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSchoolDetails();
  }, [id]);

  const fetchSchoolDetails = async () => {
    try {
      setLoading(true);
      // Use publicAPI to get school by ID
      const response = await publicAPI.getSchoolById(id);
      if (response.success) {
        setSchool(response.data);
      } else {
        setError('School not found');
      }
    } catch (err) {
      console.error('Error fetching school:', err);
      setError('Failed to load school details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <Header />
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-blue-800 to-indigo-900 text-white pt-12 pb-20">
        <div className="container mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 text-blue-200 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Schools
          </Link>
          
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="p-4 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl animate-pulse">
              <Building className="w-16 h-16 text-blue-200" />
            </div>
            <div className="flex-1 w-full">
              {loading ? (
                <div className="animate-pulse space-y-4">
                  <div className="h-10 bg-white/20 rounded-xl w-1/2"></div>
                  <div className="h-6 bg-white/15 rounded-xl w-3/4"></div>
                  <div className="flex gap-4">
                    <div className="h-8 bg-white/10 rounded-full w-24"></div>
                    <div className="h-8 bg-white/10 rounded-full w-32"></div>
                  </div>
                </div>
              ) : (
                <>
                  <h1 className="text-3xl md:text-5xl font-bold mb-4">{school?.name}</h1>
                  <p className="text-blue-100 max-w-3xl text-lg leading-relaxed mb-6">
                    {school?.description || 'Welcome to our school. Explore our departments and learning materials below.'}
                  </p>
                  
                  <div className="flex flex-wrap items-center gap-4">
                    <span className="px-4 py-1.5 bg-white/10 border border-white/20 rounded-full text-sm font-medium backdrop-blur-sm">
                      Code: {school?.code}
                    </span>
                    {school?.contactEmail && (
                      <a href={`mailto:${school.contactEmail}`} className="flex items-center gap-2 px-4 py-1.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full text-sm font-medium transition-colors">
                        <Mail className="w-4 h-4" />
                        {school.contactEmail}
                      </a>
                    )}
                    {school?.contactPhone && (
                      <div className="flex items-center gap-2 px-4 py-1.5 bg-white/10 border border-white/20 rounded-full text-sm font-medium">
                        <Phone className="w-4 h-4" />
                        {school.contactPhone}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-10">
        {/* Statistics Cards */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 animate-pulse">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 flex items-center gap-4 animate-pulse">
                <div className="p-3 bg-gray-100 rounded-lg w-14 h-14"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-100 rounded w-1/2"></div>
                  <div className="h-6 bg-gray-200 rounded w-12"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <FileText className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Total Materials</p>
                  <span className="text-3xl font-bold text-gray-900">{school?.stats?.materials || 0}</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-50 rounded-lg">
                  <Users className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Active Teachers</p>
                  <span className="text-3xl font-bold text-gray-900">{school?.stats?.teachers || 0}</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-50 rounded-lg">
                  <BookOpen className="w-8 h-8 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Departments</p>
                  <span className="text-3xl font-bold text-gray-900">{school?.departments?.length || 0}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Departments List Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <GraduationCap className="w-7 h-7 text-blue-600" />
              Available Departments
            </h2>
            <span className="text-sm text-gray-500">
              Select a department to view years
            </span>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-2xl shadow-sm border border-gray-100 h-[200px] p-6 flex flex-col justify-between">
                  <div>
                    <div className="w-10 h-10 bg-gray-100 rounded-xl mb-4"></div>
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-150 rounded w-5/6"></div>
                  </div>
                  <div className="pt-4 border-t border-gray-50 flex justify-between items-center">
                    <div className="h-4 bg-gray-100 rounded w-12"></div>
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : school?.departments && school.departments.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {school.departments.map((dept) => (
                <Link 
                  key={dept._id || dept.id} 
                  to={`/school/${school._id || school.id}/department/${dept._id || dept.id}`}
                  className="group relative bg-white rounded-2xl shadow-sm hover:shadow-xl border border-gray-100 hover:border-blue-200 transition-all duration-300 overflow-hidden flex flex-col h-full"
                >
                  {/* Decorative top bar */}
                  <div className="h-1.5 w-full bg-gradient-to-r from-blue-500 to-indigo-600"></div>
                  
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex justify-between items-start mb-4">
                      <div className="p-2.5 bg-blue-50 rounded-xl group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                        <BookOpen className="w-6 h-6 text-blue-600 group-hover:text-white" />
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-blue-50 p-1.5 rounded-full">
                        <ChevronRight className="w-4 h-4 text-blue-600" />
                      </div>
                    </div>
                    
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-700 transition-colors">
                      {dept.name}
                    </h3>
                    
                    <p className="text-sm text-gray-600 mb-6 line-clamp-2 flex-1">
                      {dept.description || 'Access course materials, lecture notes, and resources for this department.'}
                    </p>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-gray-50 mt-auto">
                      <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {dept.code}
                      </span>
                      <span className="text-sm font-medium text-blue-600 group-hover:translate-x-1 transition-transform inline-flex items-center">
                        View Resources
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-16 text-center">
              <div className="bg-gray-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Departments Found</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                This school currently has no active departments listed. Please check back later or contact the school administration.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchoolPage;
